import React, {useRef, useState} from 'react'
import ReactDiffViewer from 'react-diff-viewer'
import ToggleButton from "../../../UI/ToggleButton/ToggleButton"
import ToolBar from "../../../UI/ToolBar/ToolBar";
import AceEditor from "react-ace"
import {
    faAlignLeft,
    faSortAmountDown,
    faTrashAlt
} from '@fortawesome/free-solid-svg-icons'
import 'ace-builds/webpack-resolver'

import useLocalStorage from "../../../Hooks/useLocalStorage"
import styles from './JsonDiff.module.scss'
import './ace-editor-fixes.scss'
import Buttons from "../../../UI/ToolBar/Buttons";
import sample1Json from "./sample1.json";
import sample2Json from "./sample2.json";

JsonDiff.propTypes = {}

function JsonDiff() {
    const leftEditorStorage = 'left-editor-diff-storage'
    const rightEditorStorage = 'right-editor-diff-storage'
    const [leftLocalStorage, setLeftLocalStorage] = useLocalStorage(leftEditorStorage)
    const [rightLocalStorage, setRightLocalStorage] = useLocalStorage(rightEditorStorage)
    const initContainerRef = useRef()
    const leftEditor = useRef()
    const rightEditor = useRef()
    const [leftText, setLeftText] = useState(leftLocalStorage)
    const [rightText, setRightText] = useState(rightLocalStorage)
    const [leftError, setLeftError] = useState()
    const [rightError, setRightError] = useState()
    const [sampleDataLoaded, setSampleDataLoaded] = useState()
    const [diffSplitView, setDiffSplitView] = useLocalStorage('splitView')
    const [darkTheme, setDarkTheme] = useLocalStorage('darkTheme')
    if (!diffSplitView) setDiffSplitView('True');
    if (!darkTheme) setDarkTheme('False');

    function handleSampleDataRequest() {
        if(!sampleDataLoaded) setSampleDataLoaded(true);
        else setSampleDataLoaded(false);
        let data = sampleDataLoaded ? sample1Json : sample2Json;
        setLeftText(JSON.stringify(data, null, 2))
        setRightText(JSON.stringify(data, null, 2))

    }

    function handleReset() {
        setLeftError()
        setRightError()
        setLeftText('')
        setRightText('')
        setLeftLocalStorage('')
        setRightLocalStorage('')
    }

    function focusEditor(editor, line) {
        editor.gotoLine(line)
        editor.focus()
    }

    function diffLineClick(e) {
        let editor
        let [letter, line] = e.split('-')
        switch (letter) {
            case 'R':
                editor = rightEditor.current.editor
                break
            case 'L':
                editor = leftEditor.current.editor
                break
            default:
                editor = rightEditor.current.editor
        }

        initContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start"
        })

        focusEditor(editor, line)

    }

    function editorHasErrors(editor) {
        return Boolean(editor.current.editor.getSession().getAnnotations().length)
    }

    function editorIsNotEmpty(editor) {
        return Boolean(editor.current.editor.getValue())
    }

    function isInputValid(editor) {
        return editorIsNotEmpty(editor) && !editorHasErrors(editor)
    }

    /**
     * @param {string} value
     * @param {number} space
     */
    function formatJson(value, space) {
        return JSON.stringify(JSON.parse(value), null, space)
    }

    function handleFormat() {
        if (isInputValid(leftEditor)) {
            setLeftText(value => {
                let result = formatJson(value, 2)
                setLeftLocalStorage(result)
                return result
            })
        }
        if (isInputValid(rightEditor)) {
            setRightText(value => {
                let result = formatJson(value, 2)
                setRightLocalStorage(result)
                return result
            })
        }
    }


    function sortJsonKeys(value) {
        function getSortedKeys(object) {
            return Object.keys(object).sort((alpha, beta) => alpha.localeCompare(beta))
        }

        function isObject(value) {
            return value !== null && typeof value === "object" && !Array.isArray(value)
        }

        if (!isObject(value)) {
            if (Array.isArray(value)) {
                return value.map(item => sortJsonKeys(item))
            }
            return value
        }

        const keys = getSortedKeys(value)
        if (!keys.length) return value

        return keys.reduce((sorted, key) => {
            sorted[key] = value[key]

            if (isObject(value[key])) {
                sorted[key] = sortJsonKeys(value[key])
                return sorted
            }

            if (Array.isArray(value[key])) {
                sorted[key] = sortJsonKeys(value[key].sort())
                return sorted
            }

            return sorted
        }, {})
    }

    function handleSort() {
        let right = rightEditor.current.editor
        let left = leftEditor.current.editor
        if (isInputValid(rightEditor)) right.setValue(JSON.stringify(sortJsonKeys(JSON.parse(right.getValue())), null, 2), -1)
        if (isInputValid(leftEditor)) left.setValue(JSON.stringify(sortJsonKeys(JSON.parse(left.getValue())), null, 2), -1)

    }

    function handleChangeLeft(text) {
        setLeftText(text)
        setLeftLocalStorage(text)
    }

    function handleChangeRight(text) {
        setRightText(text)
        setRightLocalStorage(text)
    }

    function handleToggle(togglerFunction) {
        togglerFunction(state => state === 'True' ? 'False' : 'True')
    }

    const buttons =
        [
            {
                tip: "Load sample data",
                clickHandler: handleSampleDataRequest,
                text: "Sample",
            },
            {
                tip: "Reset",
                clickHandler: handleReset,
                icon: faTrashAlt
            },
            {
                tip: "Format",
                clickHandler: handleFormat,
                icon: faAlignLeft
            },
            {
                tip: "Sort",
                clickHandler: handleSort,
                icon: faSortAmountDown
            }
        ]


    let newStyles = {
        variables: {
            dark: {
                diffViewerBackground: '#0B0B0B',
                diffViewerColor: '#FFF',
                addedBackground: 'rgba(93,238,125,0.31)',
                addedColor: 'white',
                removedBackground: '#732c35',
                removedColor: 'white',
                wordAddedBackground: '#4b8343',
                wordRemovedBackground: '#a9374e',
                addedGutterBackground: '#034148',
                removedGutterBackground: '#632b30',
                gutterBackground: '#2c2f3a',
                gutterBackgroundDark: '#262933',
                highlightBackground: '#2a3967',
                highlightGutterBackground: '#2d4077',
                codeFoldGutterBackground: '#21232b',
                codeFoldBackground: '#262831',
                emptyLineBackground: '#030303',
                gutterColor: '#ffffff',
                addedGutterColor: '#eeeaea',
                removedGutterColor: '#c5c5c5',
                codeFoldContentColor: '#555a7b',
                diffViewerTitleBackground: '#2f323e',
                diffViewerTitleColor: '#555a7b',
                diffViewerTitleBorderColor: '#353846',
            }
        }
    }


    return (
        <>
            <ToolBar >
                <Buttons buttons={buttons} />
            </ToolBar>
            <div className={styles.initContainer + ` ${!leftText && !rightText ? styles.fullHeight : null}`} ref={initContainerRef}>
                <div className={styles.left + ` ${leftError ? styles.error : null}`}>
                    <AceEditor
                        className={styles.editor}
                        ref={leftEditor}
                        mode="json"
                        width="100%"
                        theme="monokai"
                        onChange={handleChangeLeft}
                        wrapEnabled={true}
                        value={leftText}
                        showGutter={true}
                        showPrintMargin={true}
                        highlightActiveLine={true}
                        autoScrollEditorIntoView={true}
                        focus={true}
                        //lines={49}
                        //minLines={49}
                        //maxLines={49}
                    />
                </div>
                <div className={styles.right + ` ${rightError ? styles.error : null}`}>
                    <AceEditor
                        className={styles.editor}
                        ref={rightEditor}
                        mode="json"
                        width="100%"
                        theme="monokai"
                        onChange={handleChangeRight}
                        value={rightText}
                        showGutter={true}
                        showPrintMargin={true}
                        highlightActiveLine={true}
                        autoScrollEditorIntoView={true}
                        //lines={49}
                        //minLines={49}
                        //maxLines={49}
                    />
                </div>
            </div>
            <div className={styles.diffContainer + ` ${!leftText && !rightText ? styles.hidden : null}`}>
                <div className={styles.center}><label>Split view</label>
                    <ToggleButton
                        size={'small'}
                        onToggle={() => handleToggle(setDiffSplitView)}
                        status={diffSplitView === 'True'}
                    />
                    <label>Dark Theme</label>
                    <ToggleButton
                        size={'small'}
                        onToggle={() => handleToggle(setDarkTheme)}
                        status={darkTheme === 'True'}
                    />
                </div>
                <ReactDiffViewer
                    oldValue={leftText}
                    newValue={rightText}
                    splitView={diffSplitView === 'True'}
                    useDarkTheme={darkTheme === 'True'}
                    styles={newStyles}
                    onLineNumberClick={diffLineClick}
                />
            </div>
        </>
    )
}

export default JsonDiff